import {Action, Attributes, AttributesRow, Card, DataStatus} from 'platform/components';
import {Hide, Link, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {equals, isNil, not} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery, useGetCurrenciesQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetPaymentTypeEnumQuery} from '@dms/api/finance';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {GetDeliveryNoteResponse, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {getCustomerName, useGetSupplierCustomersQuery, usePermissions} from '@dms/shared';
import {getSupplierById, getWarehouseById} from '@dms/warehouse/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
} from 'shared/web';

import {BasicInformationForm} from './BasicInformationForm';
import {BasicInformationReadOnly} from './BasicInformationReadOnly';
import {BranchBasedInformationForm} from './BranchBasedInformationForm';
import {BranchBasedInformationReadOnly} from './BranchBasedInformationReadOnly';

interface GeneralInformationTabProps extends RequiredTestIdProps {
  deliveryNoteId: string;
  deliveryNote: GetDeliveryNoteResponse | Nullish;
  isLoading: boolean;
  hasError: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function GeneralInformationTab(props: GeneralInformationTabProps) {
  const navigate = useNavigate();

  const dateTimeFormatter = useDateTimeFormatter();

  const [canReadReceiveNote, canEditDeliveryNote] = usePermissions({
    permissionKeys: ['receiveNoteRead', 'deliveryNoteEdit'],
  });

  const [isReadOnly, setIsReadOnly] = useState<boolean>(isNotNilOrEmpty(props.deliveryNoteId));

  const isBranchBased =
    props.deliveryNote?.type === 'TRANSFER' ||
    props.deliveryNote?.type === 'EXTERNAL_TRANSFER' ||
    props.deliveryNote?.type === 'OPENING_BALANCE';

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSupplierCustomersQuery();

  const {
    data: branchList,
    isLoading: isBranchListLoading,
    isError: hasBranchListError,
  } = useGetBranchListQuery();

  const {
    data: warehouses,
    isLoading: areWarehousesLoading,
    isError: hasWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: currencies,
    isLoading: areCurrenciesLoading,
    isError: hasCurrenciesError,
  } = useGetCurrenciesQuery();

  const {
    data: paymentTypes,
    isLoading: arePaymentTypesLoading,
    isError: hasPaymentTypesError,
  } = useGetPaymentTypeEnumQuery();

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfiles,
    isLoading: areAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const isLoading =
    props.isLoading ||
    areWarehousesLoading ||
    areSuppliersLoading ||
    isBranchListLoading ||
    areCurrenciesLoading ||
    arePaymentTypesLoading ||
    isTenantLoading ||
    areAuthorizationProfilesLoading;

  const hasError =
    props.hasError ||
    hasWarehousesError ||
    hasSuppliersError ||
    hasBranchListError ||
    hasCurrenciesError ||
    hasPaymentTypesError ||
    hasTenantError ||
    hasAuthorizationProfilesError;

  const isDeliveryNoteProcessing = props.deliveryNote?.state === 'PROCESSING';
  const isDeliveryNoteReceived = props.deliveryNote?.state === 'RECEIVED';
  const isDeliveryNoteCompleted = props.deliveryNote?.state === 'COMPLETED';

  const supplierByDeliveryNote = getSupplierById(suppliers ?? [], props.deliveryNote?.supplierId);

  const supplierByReceiveNote = getSupplierById(
    suppliers ?? [],
    props.deliveryNote?.receiveNote?.supplierId
  );

  const sourceBranchByDeliveryNote = branchList?.branchListItems?.find((branch) =>
    equals(branch.id, props.deliveryNote?.transferSourceBranchId)
  );

  const branchByDeliveryNote = branchList?.branchListItems?.find((branch) =>
    equals(branch.id, props.deliveryNote?.branchId)
  );

  const warehouseByDeliveryNote = getWarehouseById(warehouses, props.deliveryNote?.warehouseId);

  const warehouseByReceiveNote = getWarehouseById(
    warehouses,
    props.deliveryNote?.receiveNote?.warehouseId
  );

  const paymentTypeByDeliveryNote = paymentTypes?.find((paymentType) =>
    equals(paymentType.key, props.deliveryNote?.paymentType)
  );

  const authorizationProfileByDeliveryNote = authorizationProfiles?.find((authorizationProfile) =>
    equals(authorizationProfile.id, props.deliveryNote?.authorizationProfileId)
  );

  const formatToShortDate = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const redirectToReceiveNoteDetail = () => {
    if (isNilOrEmpty(props.deliveryNote?.receiveNote?.receiveNoteId)) {
      throw new Error('Missing receiveNoteId');
    }

    navigate(
      composePath(warehouseRoutes.receiveNoteDetailOverview, {
        params: {id: props.deliveryNote?.receiveNote?.receiveNoteId as string},
        isSearchParamsPreserved: false,
      })
    );
  };

  const hasReceiveNoteId = isNotNilOrEmpty(props.deliveryNote?.receiveNote?.receiveNoteId);
  const isReceiveNoteButtonDisabled = not(canReadReceiveNote) || not(hasReceiveNoteId);

  const receiveNoteTooltipLabel = match({canReadReceiveNote, hasReceiveNoteId})
    .with({hasReceiveNoteId: false}, () => i18n.t('entity.warehouse.labels.noReceiveNoteConnected'))
    .with({canReadReceiveNote: false}, () => i18n.t('general.labels.noPermission'))
    .otherwise(() => '');

  const receiveNoteActions: Action[] = [
    {
      isDisabled: isReceiveNoteButtonDisabled,
      type: 'buttonWithTooltip',
      variant: 'link',
      leftIcon: 'image/edit',
      title: i18n.t('general.actions.detail'),
      onClick: redirectToReceiveNoteDetail,
      tooltip: {
        label: receiveNoteTooltipLabel,
        isDisabled: not(isReceiveNoteButtonDisabled),
      },
    },
  ];

  const actions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'image/edit',
      title: i18n.t('general.actions.edit'),
      isDisabled: not(canEditDeliveryNote) || isDeliveryNoteReceived || isDeliveryNoteProcessing,
      onClick: () => setIsReadOnly(false),
    },
  ];

  const receiveNoteAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: isNotNilOrEmpty(props.deliveryNote?.receiveNote?.receiveNoteId) ? (
        <Link
          title={props.deliveryNote?.receiveNote?.receiveNoteNumber ?? EMPTY_PLACEHOLDER}
          rightIcon="action/open_in_new"
          size="small"
          onClick={() =>
            window.open(
              composePath(warehouseRoutes.receiveNoteDetailOverview, {
                params: {id: props.deliveryNote?.receiveNote?.receiveNoteId},
              }),
              '_blank'
            )
          }
          data-testid={suffixTestId('receiveNoteLink', props)}
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: warehouseByReceiveNote?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplier'),
      value: getCustomerName(supplierByReceiveNote),
    },
    {
      label: i18n.t('entity.warehouse.labels.deliveryType'),
      value: match(props.deliveryNote?.type)
        .with('OPENING_BALANCE', () => i18n.t('entity.warehouse.labels.openingBalance'))
        .with('PURCHASE', () => i18n.t('entity.warehouse.labels.purchase'))
        .with('TRANSFER', () => i18n.t('entity.warehouse.labels.transfer'))
        .with('EXTERNAL_TRANSFER', () => i18n.t('entity.warehouse.labels.externalTransfer'))
        .with('CORE_EXCHANGE', () => i18n.t('entity.warehouse.labels.coreExchange'))
        .otherwise(() => EMPTY_PLACEHOLDER),
    },
    {
      label: i18n.t('entity.warehouse.labels.invoiceNumber'),
      value: props.deliveryNote?.receiveNote?.invoiceNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.invoiceIssueDate'),
      value: formatToShortDate(props.deliveryNote?.receiveNote?.invoiceIssueDate),
    },
    {
      label: i18n.t('general.labels.createdAt'),
      value: formatToShortDate(props.deliveryNote?.receiveNote?.createdAt),
    },
  ];

  const renderBasicInformation = (isReadOnly: boolean, isBranchBased: boolean) =>
    match({isReadOnly, isBranchBased})
      .with({isReadOnly: true, isBranchBased: true}, () => (
        <BranchBasedInformationReadOnly
          deliveryNote={props.deliveryNote}
          sourceBranch={sourceBranchByDeliveryNote}
          branch={branchByDeliveryNote}
          authorizationProfile={authorizationProfileByDeliveryNote}
          warehouse={warehouseByDeliveryNote}
          paymentType={paymentTypeByDeliveryNote}
          data-testid={testIds.warehouse.deliveryNoteDetailOverview('basicInformationReadOnly')}
        />
      ))
      .with({isReadOnly: true, isBranchBased: false}, () => (
        <BasicInformationReadOnly
          deliveryNote={props.deliveryNote}
          supplier={supplierByDeliveryNote}
          warehouse={warehouseByDeliveryNote}
          authorizationProfile={authorizationProfileByDeliveryNote}
          paymentType={paymentTypeByDeliveryNote}
        />
      ))
      .with({isReadOnly: false, isBranchBased: true}, () => (
        <BranchBasedInformationForm
          isCompleted={isDeliveryNoteCompleted}
          deliveryNote={props.deliveryNote}
          branchList={branchList}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          onClose={() => setIsReadOnly(true)}
          data-testid={testIds.warehouse.deliveryNoteDetailOverview('form.basicInformation')}
        />
      ))
      .with({isReadOnly: false, isBranchBased: false}, () => (
        <BasicInformationForm
          isCompleted={isDeliveryNoteCompleted}
          deliveryNote={props.deliveryNote}
          suppliers={suppliers}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          currencies={currencies}
          onClose={() => setIsReadOnly(true)}
          data-testid={testIds.warehouse.deliveryNoteDetailOverview('form.basicInformation')}
        />
      ))
      .exhaustive();

  return (
    <DataStatus isLoading={isLoading} isError={hasError} minHeight="100vh">
      <VStack spacing={4}>
        <Card
          title={i18n.t('general.labels.basicInformation')}
          actions={isReadOnly ? actions : undefined}
          data-testid={suffixTestId('card.basicInformation', props)}
        >
          {renderBasicInformation(isReadOnly, isBranchBased)}
        </Card>
        <Hide when={not(canReadReceiveNote) || isNilOrEmpty(props.deliveryNote?.receiveNote)}>
          <Card
            title={i18n.t('entity.warehouse.labels.receiveNote')}
            actions={receiveNoteActions}
            data-testid={suffixTestId('cards.receiveNote', props)}
          >
            <Card variant="inlineGrey">
              <Attributes
                rows={receiveNoteAttributes}
                size="quarter"
                data-testid={suffixTestId('receiveNoteAttributes', props)}
              />
            </Card>
          </Card>
        </Hide>
      </VStack>
    </DataStatus>
  );
}
